import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { blue } from '@/lib/colours';

/** Approved BLS-254 / ILRX-4438 wording — do not paraphrase. */
export const TRAIT_LEADER_EXPLANATION =
  'BREEDPLAN trait leaders are published sires that meet additional breed-defined criteria, including ranking within a specified top percentile for a trait and achieving a minimum level of EBV accuracy, with exact requirements varying by breed and trait.';

/**
 * `traitLeader` is a bitstring keyed by EBV field number (1-based):
 * character at index `fieldNumber - 1` is `'1'` when the animal is a trait leader
 * for that EBV.
 */
export function isTraitLeaderForEbvField(
  traitLeader: string | null | undefined,
  fieldNumber: number,
): boolean {
  const index = fieldNumber - 1;
  if (index < 0 || !traitLeader) return false;
  return traitLeader.length > index && traitLeader[index] === '1';
}

/** Background / border matching search-grid `.ebv-trait-leader` cells. */
const traitLeaderCellSx: SxProps<Theme> = (theme) => ({
  backgroundColor: blue[200],
  border: `1px solid ${theme.palette.divider}`,
  boxSizing: 'border-box',
  ...theme.applyStyles('dark', {
    backgroundColor: alpha(blue[400], 0.2),
  }),
});

function resolveSx(
  sx: SxProps<Theme> | undefined,
  theme: Theme,
): Record<string, unknown> {
  if (!sx) return {};
  if (typeof sx === 'function') {
    return (sx(theme) ?? {}) as Record<string, unknown>;
  }
  if (Array.isArray(sx)) {
    return sx.reduce<Record<string, unknown>>((acc, part) => {
      if (!part || part === true) return acc;
      return { ...acc, ...resolveSx(part as SxProps<Theme>, theme) };
    }, {});
  }
  return sx as Record<string, unknown>;
}

/** Merge category cell sx with optional trait-leader tint (typed for TableCell). */
export function withTraitLeaderCellSx(
  base: SxProps<Theme>,
  isTraitLeader: boolean,
): SxProps<Theme> {
  if (!isTraitLeader) return base;
  return (theme) => ({
    ...resolveSx(base, theme),
    ...resolveSx(traitLeaderCellSx, theme),
  });
}
